<template>
  <div>
    <data-table-card item-add-route-name="administration-customer-add"
                     item-route-name="administration-customer"
                     sort-by="companyName"
                     v-bind:filter="filter"
                     v-bind:headers="headers"
                     v-bind:items="filteredItems"
                     v-bind:title="$t('customers')">
      <template v-slot:filter>
        <v-select class="sf-search sf-customer-list__rep"
                  clearable
                  dense
                  flat
                  hide-details
                  solo
                  v-bind:placeholder="$t('fieldSalesRep')"
                  v-bind:items="reps"
                  v-model="fieldSalesRepFilter" />
      </template>
      <template v-slot:item.companyName="{ item }">
        <span class="font-weight-medium">{{ item.companyName | defaultValue($t('untitled')) }}</span>
      </template>
      <template v-slot:item.location="{ item }">
        {{ item.location }}
      </template>
      <template v-slot:item.orderCount="{ item }">
        <span class="sf-tabular">{{ item.orderCount }}</span>
      </template>
      <template v-slot:item.state="{ item }">
        <state-chip v-bind:state="item.state" />
      </template>
      <template v-slot:item.wasAttending="{ item }">
        <v-icon color="success"
                small
                v-bind:title="$t('onSite')"
                v-if="item.wasAttending">ti-circle-check</v-icon>
        <span class="sf-muted"
              v-else>–</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="sf-row-actions">
          <v-btn icon
                 small
                 v-bind:title="$t('signInAsCustomer')"
                 v-bind:to="{ name: 'home', query: { invitationCode: item.invitationCode }}"
                 v-if="item.state === 'accepted'"
                 v-on:click.native.stop>
            <v-icon small>ti-login-2</v-icon>
          </v-btn>
          <v-btn icon
                 small
                 v-bind:title="$t('sendEmail')"
                 v-on:click.stop="sendEmailMessage(item.id)">
            <v-icon small>ti-mail-forward</v-icon>
          </v-btn>
        </div>
      </template>
    </data-table-card>
  </div>
</template>

<script>
  import DataTableCard from '@/components/common/DataTableCard'
  import StateChip from '@/components/administration/StateChip'

  export default {
    components: {
      DataTableCard,
      StateChip
    },
    data: () => ({
      fieldSalesRepFilter: null
    }),
    computed: {
      headers() {
        return [
          { text: this.$t('customerNumber'), value: 'id', width: 120 },
          { text: this.$t('companyName'), value: 'companyName' },
          { text: this.$t('location'), value: 'location' },
          { text: this.$t('fieldSalesRep'), value: 'fieldSalesRep' },
          { text: this.$t('orders'), value: 'orderCount', align: 'end', width: 130 },
          { text: this.$t('state'), value: 'state', width: 140 },
          { text: this.$t('onSite'), value: 'wasAttending', align: 'center', width: 100 },
          { text: '', value: 'actions', align: 'end', sortable: false, filterable: false, width: 110 }
        ]
      },
      reps() {
        const reps = this.customers
          .map(c => c.fieldSalesRep)
          .filter((v, i, a) => v != null && a.indexOf(v) === i)
          .sort()

        return reps.map(r => ({ text: r, value: r }))
      },
      filteredItems() {
        let items = this.items

        if (this.fieldSalesRepFilter != null) {
          items = items.filter(i => i.fieldSalesRep === this.fieldSalesRepFilter)
        }

        return items
      },
      filter() {
        return {
          property: 'state',
          values: [
            {
              name: 'all',
              showAll: true,
              title: this.$t('all')
            },
            {
              icon: 'ti-clock',
              name: 'open',
              title: this.$t('open')
            },
            {
              icon: 'ti-mail-check',
              name: 'invited',
              title: this.$t('invited')
            },
            {
              icon: 'ti-check',
              name: 'accepted',
              title: this.$t('accepted')
            },
            {
              icon: 'ti-x',
              name: 'declined',
              title: this.$t('declined')
            },
            {
              icon: 'ti-map-pin-check',
              name: 'on-site',
              title: this.$t('onSite')
            }
          ]
        }
      },
      items() {
        return [...this.customers].map(c => ({
          ...c,
          location: [ c.postalCode, c.city ].filter(v => v != null && v !== '').join(' ')
        }))
      }
    },
    methods: {
      sendEmailMessage(id) {
        this.$emit('send-email-message', { id })
      }
    },
    name: 'CustomerList',
    props: {
      customers: Array
    }
  }
</script>

<style scoped>
  .sf-customer-list__rep {
    flex: 1 1 160px;
    max-width: 240px;
  }

  .sf-row-actions {
    display: inline-flex;
    gap: 4px;
    white-space: nowrap;
  }
</style>

<i18n>
  {
    "de": {
      "accepted": "Akzeptiert",
      "all": "Alle",
      "companyName": "Firma",
      "customerNumber": "Kundennr.",
      "customers": "Kunden",
      "declined": "Abgelehnt",
      "fieldSalesRep": "Außendienst",
      "invited": "Eingeladen",
      "location": "Ort",
      "onSite": "Vor Ort",
      "open": "Offen",
      "orders": "Bestellungen",
      "sendEmail": "E-Mail senden",
      "signInAsCustomer": "Als Kunde anmelden",
      "state": "Status",
      "untitled": "Unbenannt"
    },
    "en": {
      "accepted": "Accepted",
      "all": "All",
      "companyName": "Company",
      "customerNumber": "Customer no.",
      "customers": "Customers",
      "declined": "Declined",
      "fieldSalesRep": "Field sales rep",
      "invited": "Invited",
      "location": "Location",
      "onSite": "On site",
      "open": "Open",
      "orders": "Orders",
      "sendEmail": "Send email",
      "signInAsCustomer": "Sign in as customer",
      "state": "State",
      "untitled": "Untitled"
    }
  }
</i18n>
